// Money: integer minor units plus a currency code, never a float
// (02 §2.7 rule 6, 06 §6.1). Every function here is exact: rational maths runs on bigint.

export const CURRENCY_EXPONENT = {
  AED: 2,
  SAR: 2,
  QAR: 2,
  PHP: 2,
  INR: 2,
  USD: 2,
  KWD: 3,
  BHD: 3,
  OMR: 3,
} as const;

export type CurrencyCode = keyof typeof CURRENCY_EXPONENT;

export const CURRENCY_CODES = Object.keys(CURRENCY_EXPONENT) as readonly CurrencyCode[];

export interface Money {
  readonly amountMinor: number;
  readonly currency: CurrencyCode;
}

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyError';
  }
}

export function isCurrencyCode(value: unknown): value is CurrencyCode {
  return typeof value === 'string' && Object.hasOwn(CURRENCY_EXPONENT, value);
}

export function minorUnitExponent(currency: CurrencyCode): number {
  return CURRENCY_EXPONENT[currency];
}

/** Builds a Money, rejecting non-integers, NaN, Infinity and unsafe integers. */
export function money(amountMinor: number, currency: CurrencyCode): Money {
  if (!isCurrencyCode(currency)) throw new MoneyError(`Unknown currency: ${String(currency)}`);
  if (!Number.isSafeInteger(amountMinor)) {
    throw new MoneyError(`Amount must be a safe integer of minor units, got ${amountMinor}`);
  }
  // Normalise -0 so equality checks and JSON stay predictable.
  return { amountMinor: amountMinor === 0 ? 0 : amountMinor, currency };
}

export function isMoney(value: unknown): value is Money {
  if (typeof value !== 'object' || value === null) return false;
  const { amountMinor, currency } = value as Record<string, unknown>;
  return Number.isSafeInteger(amountMinor) && isCurrencyCode(currency);
}

export function zeroMoney(currency: CurrencyCode): Money {
  return money(0, currency);
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new MoneyError(`Currency mismatch: ${a.currency} and ${b.currency}`);
  }
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor + b.amountMinor, a.currency);
}

export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor - b.amountMinor, a.currency);
}

export function negateMoney(a: Money): Money {
  return money(-a.amountMinor, a.currency);
}

export function multiplyMoney(a: Money, quantity: number): Money {
  if (!Number.isSafeInteger(quantity))
    throw new MoneyError(`Quantity must be an integer, got ${quantity}`);
  return money(a.amountMinor * quantity, a.currency);
}

export function sumMoney(items: readonly Money[], currency: CurrencyCode): Money {
  return items.reduce(addMoney, zeroMoney(currency));
}

export function compareMoney(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b);
  return Math.sign(a.amountMinor - b.amountMinor) as -1 | 0 | 1;
}

export function equalsMoney(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.amountMinor === b.amountMinor;
}

// ---------------------------------------------------------------- rounding

function abs(n: bigint): bigint {
  return n < 0n ? -n : n;
}

function toBigInt(value: number | bigint, label: string): bigint {
  if (typeof value === 'bigint') return value;
  if (!Number.isSafeInteger(value))
    throw new MoneyError(`${label} must be an integer, got ${value}`);
  return BigInt(value);
}

function toSafeNumber(value: bigint): number {
  if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new MoneyError('Amount is outside the safe integer range');
  }
  return Number(value);
}

/** numerator / denominator rounded to the nearest integer, ties away from zero (half-up for amounts >= 0). */
function divideRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new MoneyError('Division by zero');
  const sign = numerator < 0n !== denominator < 0n ? -1n : 1n;
  const n = abs(numerator);
  const d = abs(denominator);
  return sign * ((2n * n + d) / (2n * d));
}

/**
 * Converts a rational to minor units, rounding half-up (06 §6.1 rule 4).
 * Ties go away from zero so a credit note mirrors its invoice exactly.
 */
export function roundHalfUp(numerator: number | bigint, denominator: number | bigint): number {
  return toSafeNumber(
    divideRoundHalfUp(toBigInt(numerator, 'Numerator'), toBigInt(denominator, 'Denominator')),
  );
}

// -------------------------------------------------------------- allocation

/**
 * Splits an amount by integer ratios so the parts always sum to the whole.
 * Largest remainder: each part gets its floor share, then the leftover minor units go to the
 * biggest fractional remainders (ties to the earlier part).
 */
export function allocateMoney(amount: Money, ratios: readonly number[]): Money[] {
  if (ratios.length === 0) throw new MoneyError('Allocation needs at least one ratio');
  const weights = ratios.map((r) => {
    if (!Number.isSafeInteger(r) || r < 0)
      throw new MoneyError(`Ratios must be non-negative integers, got ${r}`);
    return BigInt(r);
  });
  const totalWeight = weights.reduce((sum, w) => sum + w, 0n);
  if (totalWeight === 0n) throw new MoneyError('At least one ratio must be positive');

  const total = abs(BigInt(amount.amountMinor));
  const shares = weights.map((w) => ({
    floor: (total * w) / totalWeight,
    rest: (total * w) % totalWeight,
  }));
  let leftover = total - shares.reduce((sum, s) => sum + s.floor, 0n);

  const byRemainder = shares
    .map((s, index) => ({ index, rest: s.rest }))
    .sort((a, b) => (a.rest === b.rest ? a.index - b.index : a.rest > b.rest ? -1 : 1));
  const extra = new Set<number>();
  for (const { index } of byRemainder) {
    if (leftover === 0n) break;
    extra.add(index);
    leftover -= 1n;
  }

  const sign = amount.amountMinor < 0 ? -1 : 1;
  return shares.map((s, index) =>
    money(sign * toSafeNumber(s.floor + (extra.has(index) ? 1n : 0n)), amount.currency),
  );
}

export function splitMoney(amount: Money, parts: number): Money[] {
  if (!Number.isSafeInteger(parts) || parts < 1)
    throw new MoneyError(`Parts must be a positive integer, got ${parts}`);
  return allocateMoney(
    amount,
    Array.from({ length: parts }, () => 1),
  );
}

import { isIsoDate } from './time';

// ------------------------------------------------------------ decimal maths

interface Ratio {
  readonly numerator: bigint;
  readonly denominator: bigint;
}

function parseDecimal(text: string, label: string): Ratio {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(text.trim());
  if (!match) throw new MoneyError(`${label} must be a plain decimal, got "${text}"`);
  const fraction = match[2] ?? '';
  return { numerator: BigInt(match[1] + fraction), denominator: 10n ** BigInt(fraction.length) };
}

function toRatio(value: number | string, label: string): Ratio {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new MoneyError(`${label} must be finite`);
    return parseDecimal(String(value), label);
  }
  return parseDecimal(value, label);
}

// --------------------------------------------------------------------- VAT

export interface VatSplit {
  readonly net: Money;
  readonly vat: Money;
  readonly gross: Money;
}

/**
 * Backs VAT out of a VAT-inclusive price (D-18, 03 §3.3):
 * vat = round_half_up(gross * r / (100 + r)), net = gross - vat.
 * `ratePercent` is a number or decimal string such as 15 or "7.5".
 */
export function vatFromInclusive(gross: Money, ratePercent: number | string): VatSplit {
  const rate = toRatio(ratePercent, 'VAT rate');
  // gross * (rn/rd) / (100 + rn/rd) = gross * rn / (100 * rd + rn)
  const vatMinor = divideRoundHalfUp(
    BigInt(gross.amountMinor) * rate.numerator,
    100n * rate.denominator + rate.numerator,
  );
  const vat = money(toSafeNumber(vatMinor), gross.currency);
  return { net: subtractMoney(gross, vat), vat, gross };
}

// ---------------------------------------------------------------------- FX

export type RateValue =
  string | { readonly numerator: number | bigint; readonly denominator: number | bigint };

/** One dated rate: 1 major unit of `from` buys `rate` major units of `to`. */
export interface FxRate {
  readonly from: CurrencyCode;
  readonly to: CurrencyCode;
  readonly rate: RateValue;
  readonly date: string;
}

function rateToRatio(rate: RateValue): Ratio {
  const ratio =
    typeof rate === 'string'
      ? parseDecimal(rate, 'FX rate')
      : {
          numerator: toBigInt(rate.numerator, 'Numerator'),
          denominator: toBigInt(rate.denominator, 'Denominator'),
        };
  if (ratio.numerator <= 0n || ratio.denominator <= 0n)
    throw new MoneyError('FX rate must be positive');
  return ratio;
}

/**
 * Converts with an explicit dated rate using exact rational maths.
 * There is no default rate: a missing or mismatched rate throws (06 §6.1 rule 7).
 */
export function convert(amount: Money, fx: FxRate | undefined): Money {
  if (fx === undefined) throw new MoneyError(`No FX rate supplied for ${amount.currency}`);
  if (fx.from !== amount.currency) {
    throw new MoneyError(`FX rate is from ${fx.from} but amount is ${amount.currency}`);
  }
  if (!isCurrencyCode(fx.to) || fx.to === fx.from)
    throw new MoneyError(`Invalid FX target currency: ${fx.to}`);
  if (!isIsoDate(fx.date)) throw new MoneyError(`FX rate needs an ISO date, got "${fx.date}"`);

  const { numerator, denominator } = rateToRatio(fx.rate);
  const scaleUp = 10n ** BigInt(CURRENCY_EXPONENT[fx.to]);
  const scaleDown = 10n ** BigInt(CURRENCY_EXPONENT[fx.from]);
  const minor = divideRoundHalfUp(
    BigInt(amount.amountMinor) * numerator * scaleUp,
    denominator * scaleDown,
  );
  return money(toSafeNumber(minor), fx.to);
}

// ----------------------------------------------------------------- display

/** Display only. Never parse this back into an amount. */
export function formatMoney(amount: Money, locale = 'en'): string {
  const exponent = CURRENCY_EXPONENT[amount.currency];
  const digits = abs(BigInt(amount.amountMinor))
    .toString()
    .padStart(exponent + 1, '0');
  const decimal = `${digits.slice(0, digits.length - exponent)}.${digits.slice(digits.length - exponent)}`;
  const signed = amount.amountMinor < 0 ? `-${decimal}` : decimal;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: amount.currency,
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  }).format(signed as `${number}`);
}
